import { uiText } from "@/lib/i18n/uiText";
import { useParams, Navigate, Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PUBLIC_PREDICTION_COLUMNS, PREDICTIONS_READ } from "@/lib/predictionColumns";
import { useSEO } from "@/lib/useSEO";
import {
  parseFixtureSlug,
  buildFixtureSlug,
  slugify,
  clubMatcherFromSlug,
  competitionForLeague,
} from "@/lib/entities";
import {
  fixtureCrumbs,
  fixtureMeta,
  fixtureJsonLd,
  faqPageJsonLd,
  type FixtureInput,
} from "@/lib/seoSchema";
import { PredictionCard } from "@/components/predictions/PredictionCard";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { InternalLinks } from "@/components/seo/InternalLinks";
import { HistoricalCrossLinks } from "@/components/seo/HistoricalCrossLinks";
import { ContextualPredictionLinks } from "@/components/seo/ContextualPredictionLinks";
import type { Prediction } from "@/types";

function useFixture(slug: string) {
  const parsed = parseFixtureSlug(slug);
  return useQuery({
    queryKey: ["fixture", slug],
    enabled: !!parsed,
    queryFn: async () => {
      // Primary path: the slug carries the exact match date, so scope the
      // query to that single UTC day. This keeps past fixtures — including
      // evaluated "Recent Winners" — reachable, instead of only upcoming ones.
      const dayStart = `${parsed!.date}T00:00:00.000Z`;
      const dayEnd = `${parsed!.date}T23:59:59.999Z`;
      const { data, error } = await supabase
        .from(PREDICTIONS_READ)
        .select(PUBLIC_PREDICTION_COLUMNS)
        .eq("status", "approved")
        .gte("kickoff_time", dayStart)
        .lte("kickoff_time", dayEnd)
        .order("kickoff_time", { ascending: true })
        .limit(2000);
      if (error) throw error;

      const buildResult = (rows: Prediction[]) => {
        // Prefer an exact slug + date match; fall back to the most recent
        // slug match regardless of date (handles rescheduled fixtures and
        // kickoffs that shifted across a UTC-day boundary).
        const exact = rows.find(
          (p) =>
            slugify(p.team_home || "") === parsed!.homeSlug &&
            slugify(p.team_away || "") === parsed!.awaySlug &&
            (p.kickoff_time || "").slice(0, 10) === parsed!.date,
        );
        const anchor =
          exact ||
          rows.find(
            (p) =>
              slugify(p.team_home || "") === parsed!.homeSlug &&
              slugify(p.team_away || "") === parsed!.awaySlug,
          );
        if (!anchor) return null;
        const groupId = anchor.match_id || anchor.id;
        const markets = rows.filter((p) => (p.match_id || p.id) === groupId);
        return { anchor, markets: markets.length ? markets : [anchor] };
      };

      const primary = buildResult(((data as unknown) as Prediction[]) || []);
      if (primary) return primary;

      // Secondary fallback: no row on the slug date. Search by team name
      // (date-agnostic) using the most distinctive slug tokens so we avoid a
      // full-table scan while still resolving rescheduled/shifted fixtures.
      const homeToken = clubMatcherFromSlug(parsed!.homeSlug);
      const awayToken = clubMatcherFromSlug(parsed!.awaySlug);
      const { data: fbData, error: fbError } = await supabase
        .from(PREDICTIONS_READ)
        .select(PUBLIC_PREDICTION_COLUMNS)
        .eq("status", "approved")
        .ilike("team_home", `%${homeToken}%`)
        .ilike("team_away", `%${awayToken}%`)
        .order("kickoff_time", { ascending: false })
        .limit(500);
      if (fbError) throw fbError;
      return buildResult(((fbData as unknown) as Prediction[]) || []);
    },
  });
}

/**
 * Closest historical (kicked-off-already) fixtures to the anchor, preferring
 * matches in the same league then falling back to sport-wide. Powers the
 * "Related Historical Odds" section — every returned row links onwards to its
 * own /fixtures preview, plus /match-preview and /analysis query views.
 */
function useRelatedHistoricalFixtures(
  anchorId: string | undefined,
  league: string | null | undefined,
  kickoff: string | null | undefined,
) {
  return useQuery({
    queryKey: ["related-historical-fixtures", anchorId, league, kickoff],
    enabled: !!anchorId && !!kickoff,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const anchorIso = kickoff!;
      const cutoff = anchorIso < nowIso ? anchorIso : nowIso;
      let q = supabase
        .from(PREDICTIONS_READ)
        .select(PUBLIC_PREDICTION_COLUMNS)
        .eq("status", "approved")
        .lt("kickoff_time", cutoff)
        .order("kickoff_time", { ascending: false })
        .limit(24);
      if (league) q = q.eq("league", league);
      const { data, error } = await q;
      if (error) throw error;
      let rows = (data as unknown as Prediction[]) || [];
      // Fallback to sport-wide if the league has no earlier fixtures.
      if (rows.length === 0 && league) {
        const { data: any, error: anyErr } = await supabase
          .from(PREDICTIONS_READ)
          .select(PUBLIC_PREDICTION_COLUMNS)
          .eq("status", "approved")
          .lt("kickoff_time", cutoff)
          .order("kickoff_time", { ascending: false })
          .limit(24);
        if (anyErr) throw anyErr;
        rows = (any as unknown as Prediction[]) || [];
      }
      // Dedupe by fixture (match_id || id) and drop the anchor itself.
      const seen = new Set<string>();
      const out: Prediction[] = [];
      for (const p of rows) {
        const key = p.match_id || p.id;
        if (!key || seen.has(key) || key === anchorId) continue;
        seen.add(key);
        out.push(p);
        if (out.length >= 6) break;
      }
      return out;
    },
  });
}

export default function FixtureDetail() {
  const { slug = "" } = useParams();
  const validSlug = !!parseFixtureSlug(slug);
  const { data, isLoading } = useFixture(slug);
  const anchorForRelated = data?.anchor;
  const { data: relatedFixtures } = useRelatedHistoricalFixtures(
    anchorForRelated?.match_id || anchorForRelated?.id,
    anchorForRelated?.league,
    anchorForRelated?.kickoff_time,
  );

  const anchor = data?.anchor;
  const markets = data?.markets ?? [];
  const home = anchor?.team_home || "Home";
  const away = anchor?.team_away || "Away";
  const fixtureName = `${home} vs ${away}`;
  const comp = competitionForLeague(anchor?.league || "");
  const canonical = anchor
    ? `/fixtures/${buildFixtureSlug(home, away, anchor.kickoff_time)}`
    : `/fixtures/${slug}`;
  const kickoff = anchor?.kickoff_time;
  const kickoffLabel = kickoff
    ? new Date(kickoff).toLocaleString("en-GB", { dateStyle: "full", timeStyle: "short" })
    : "TBC";

  // Single source of truth for fixture meta + JSON-LD (SportsEvent with
  // startDate + both competitors, NewsArticle, BreadcrumbList) — the same
  // builders the prerenderer and regression tests use, so they can never drift.
  const fixtureInput: FixtureInput | null = anchor
    ? {
        id: anchor.id,
        match_id: anchor.match_id,
        team_home: home,
        team_away: away,
        league: anchor.league,
        kickoff_time: anchor.kickoff_time,
        created_at: anchor.created_at,
        markets: markets.map((m) => ({
          market_type: m.market_type,
          predicted_outcome: m.predicted_outcome,
          odds: m.odds,
          confidence_score: m.confidence_score,
        })),
      }
    : null;

  const meta = fixtureInput ? fixtureMeta(fixtureInput) : null;
  const title = meta?.title ?? "Fixture Prediction | Statlign";
  const description =
    meta?.description ??
    "Football fixture prediction, betting tips and odds from the Statlign 50-strategy engine.";
  const keywords =
    meta?.keywords ??
    `${fixtureName} prediction, ${fixtureName} betting tips, ${fixtureName} odds`;
  const published = meta?.publishedTime ?? new Date().toISOString();
  const crumbs = fixtureInput
    ? fixtureCrumbs({
        team_home: home,
        team_away: away,
        match_id: anchor?.match_id,
        id: anchor?.id ?? "",
        league: anchor?.league,
        kickoff_time: anchor?.kickoff_time,
      })
    : [];

  const jsonLd: Record<string, unknown>[] = fixtureInput
    ? (fixtureJsonLd(fixtureInput) as unknown as Record<string, unknown>[])
    : [];

  // Attach a FAQPage node so this historical match preview is eligible for
  // FAQ rich results in Google Search. Answers use the resolved fixture data.
  if (fixtureInput) {
    jsonLd.push(
      faqPageJsonLd([
        {
          question: `What is Statlign's prediction for ${fixtureName}?`,
          answer:
            markets.length > 0 && markets[0]!.predicted_outcome
              ? `Statlign's engine predicts ${markets[0]!.predicted_outcome}${
                  markets[0]!.market_type ? ` (${markets[0]!.market_type})` : ""
                }${
                  markets[0]!.odds ? ` at odds ${markets[0]!.odds}` : ""
                } for ${fixtureName}.`
              : `Statlign's AI-powered engine analyses ${fixtureName} across 50+ strategies to produce betting predictions, odds and confidence scores.`,
        },
        {
          question: `When does ${fixtureName} kick off?`,
          answer: `${fixtureName} kicks off on ${kickoffLabel}${
            anchor?.league ? ` in the ${anchor.league}` : ""
          }.`,
        },
        {
          question: `How many markets does Statlign cover for ${fixtureName}?`,
          answer: `Statlign covers ${markets.length} market${
            markets.length === 1 ? "" : "s"
          } for this fixture, including 1X2, Over/Under, Both Teams to Score, Double Chance and Correct Score where applicable.`,
        },
        {
          question: `Is the ${fixtureName} prediction free to view?`,
          answer:
            "Yes. All approved Statlign match previews are publicly accessible without an account so search engines, AI crawlers and visitors can access the full analysis.",
        },
      ]) as unknown as Record<string, unknown>,
    );
  }

  // ItemList JSON-LD for the "Related Historical Odds" section. Each row links
  // to that fixture's own preview + the scoped /match-preview and /analysis
  // deep views, so search engines can traverse the historical archive graph.
  if (fixtureInput && relatedFixtures && relatedFixtures.length > 0) {
    const baseUrl = "https://statlign.com";
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `Related Historical Odds — ${fixtureName}`,
      description: `Nearby past fixtures${anchor?.league ? ` in the ${anchor.league}` : ""} with their own preview and analysis pages.`,
      numberOfItems: relatedFixtures.length,
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      itemListElement: relatedFixtures.map((p, i) => {
        const name = `${p.team_home} vs ${p.team_away}`;
        const fSlug = buildFixtureSlug(p.team_home!, p.team_away!, p.kickoff_time);
        const q = encodeURIComponent(name);
        return {
          "@type": "ListItem",
          position: i + 1,
          name,
          url: `${baseUrl}/fixtures/${fSlug}`,
          item: {
            "@type": "SportsEvent",
            name,
            url: `${baseUrl}/fixtures/${fSlug}`,
            ...(p.kickoff_time ? { startDate: p.kickoff_time } : {}),
            ...(p.league ? { superEvent: { "@type": "SportsOrganization", name: p.league } } : {}),
            competitor: [
              { "@type": "SportsTeam", name: p.team_home },
              { "@type": "SportsTeam", name: p.team_away },
            ],
            subjectOf: [
              { "@type": "WebPage", url: `${baseUrl}/match-preview?q=${q}`, name: `${name} — Match Preview` },
              { "@type": "WebPage", url: `${baseUrl}/analysis?q=${q}`, name: `${name} — Analysis` },
            ],
          },
        };
      }),
    } as unknown as Record<string, unknown>);
  }


  useSEO({
    title,
    description,
    canonicalPath: canonical,
    keywords,
    ogType: "article",
    publishedTime: published,
    modifiedTime: published,
    jsonLd,
    // Explicit social preview — upgrades twitter:card to summary_large_image and
    // makes crawlers emit a rich link preview for every historical fixture.
    image: "/og-image.jpg",
    imageAlt: `${fixtureName} — Statlign prediction, tips & odds`,
  });

  if (!validSlug) return <Navigate to="/predictions" replace />;

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />  {uiText("Loading fixture…")} </div>
    );
  }

  if (!anchor) {
    return (
      <div className="space-y-4 py-16 text-center">
        <h1 className="text-2xl font-bold">{uiText("Fixture not found")}</h1>
        <p className="text-muted-foreground">{uiText("This fixture may have kicked off already.")}</p>
        <Link to="/predictions" className="text-primary underline">{uiText("Browse all predictions")}</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <Breadcrumbs items={crumbs} className="text-sm text-muted-foreground" />


      <header className="space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold">{fixtureName}  {uiText("Prediction & Betting Tips")}</h2>
        <p className="text-muted-foreground">
          {anchor.league ? `${anchor.league} · ` : ""}{uiText("Kick-off:")} {kickoffLabel}
        </p>
        <p className="max-w-3xl text-muted-foreground">{description}</p>
      </header>

      <section aria-label={uiText("Market predictions")} className="space-y-4">
        <h2 className="text-xl font-semibold">{uiText("Predictions for")} {fixtureName}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {markets.map((p) => (
            <PredictionCard key={p.id} prediction={p} />
          ))}
        </div>
      </section>

      <section aria-label={uiText("Teams")} className="rounded-xl border border-border p-5">
        <h2 className="mb-3 text-lg font-semibold">{uiText("Team pages")}</h2>
        <div className="flex flex-wrap gap-2">
          <Link to={`/clubs/${slugify(home)}`} className="inline-flex rounded-full border border-border px-3 py-1 text-sm hover:bg-accent hover:text-accent-foreground">{home}</Link>
          <Link to={`/clubs/${slugify(away)}`} className="inline-flex rounded-full border border-border px-3 py-1 text-sm hover:bg-accent hover:text-accent-foreground">{away}</Link>
          {comp && (
            <Link to={`/competitions/${comp.slug}`} className="inline-flex rounded-full border border-border px-3 py-1 text-sm hover:bg-accent hover:text-accent-foreground">{uiText(comp.label)}</Link>
          )}
        </div>
      </section>

      <HistoricalCrossLinks fixtureQuery={fixtureName} />

      {relatedFixtures && relatedFixtures.length > 0 && (
        <section
          aria-label={uiText("Related historical odds")}
          data-testid="related-historical-odds"
          className="space-y-3 rounded-xl border border-border p-5"
        >

          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-lg font-semibold">{uiText("Related Historical Odds")}</h2>
            <Link to="/historical-odds" className="text-xs text-primary underline">
               {uiText("See full historical odds archive")} </Link>
          </div>
          <p className="text-sm text-muted-foreground">
             {uiText("Nearby past fixtures")}{anchor?.league ? uiText(" in the {{v0}}", { v0: anchor.league }) : ""}  {uiText("with their own preview and analysis pages.")} </p>
          <ul className="divide-y divide-border">
            {relatedFixtures.map((p) => {
              const name = `${p.team_home} vs ${p.team_away}`;
              const fSlug = buildFixtureSlug(p.team_home!, p.team_away!, p.kickoff_time);
              const q = encodeURIComponent(name);
              const when = p.kickoff_time
                ? new Date(p.kickoff_time).toLocaleDateString("en-GB", { dateStyle: "medium" })
                : "";
              return (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <Link to={`/fixtures/${fSlug}`} className="font-medium hover:text-primary">
                    {name}
                  </Link>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {when && <span>{when}</span>}
                    <Link to={`/match-preview?q=${q}`} className="text-primary hover:underline">
                       {uiText("Match preview")} </Link>
                    <Link to={`/analysis?q=${q}`} className="text-primary hover:underline">
                       {uiText("Analysis")} </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ContextualPredictionLinks
        kickoffTime={anchor?.kickoff_time}
        market={markets[0]?.market_type}
      />

      <InternalLinks />
    </div>
  );
}
